import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useChatProfileStore } from 'components/AskDelaChat/ChatProfileStore'
import {
    elementToEditorData,
    extractSuggestions,
    getUnresolvedChanges,
    hideInLineTrackTip,
    processContent,
} from 'components/WorkspaceEditor/components/ContentEditor/contentUtils'
import { useDuplicateArticleValidation } from 'components/WorkspaceEditor/features/duplicateArticleValidation'
import {
    KeyHandler,
    KeyHandlerParams,
} from 'components/WorkspaceEditor/features/keyOperations/types'
import { createKeyHandler } from 'components/WorkspaceEditor/features/keyOperations/utils'
import { usePromoteDemote } from 'components/WorkspaceEditor/features/promoteDemote'
import { CONTENT_PLACEHOLDER_TEXT } from 'components/WorkspaceEditor/features/scrollToNewContent/constants'
import {
    useContentMutationService,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import {
    ADDITIONAL_ELEMENT_SUFFIX,
    getBaseElementId,
    replaceNodeElementsByElementId,
    withDebounce,
} from 'components/WorkspaceEditor/utils'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { batchUpdate } from 'shared/utils/workspaceUtils'
import {
    useShallowSelector,
    useWorkspace2Store,
} from '../../../../Workspace2Store'
import { useKeyOperation } from '../../../../features/keyOperations/hooks/useKeyOperation'
import {
    useNodeMapActions,
    useNodeMapShallowSelector,
} from '../../../../features/keyOperations/store/NodeMapStore'
import { useContentItemViewNode } from '../../../../hooks'
import type { ClassicEditorHandler } from '../../../ContentEditor'
import { detectContentChanges } from '../plainTextChangeDetection'

/**
 * Performance-optimized hook for initial data processing
 * Reduces expensive DOM parsing operations
 */
const useOptimizedInitialData = (textElement: TextContentElement) => {
    // Cache elementToEditorData with more specific dependencies
    const initialData = useMemo(() => {
        if (!textElement?.text) return ''
        return elementToEditorData(textElement)
    }, [textElement?.id, textElement?.text]) // Only recompute if ID or text changes

    // Cache suggestions more aggressively
    const initialSuggestions = useMemo(() => {
        if (!textElement?.id) return []
        return extractSuggestions([textElement])
    }, [textElement.id, textElement.changeType, textElement.authoredUserId])

    return { initialData, initialSuggestions }
}

interface UsePlainTextHandlersParams {
    textElement: TextContentElement
    contentItem: Partial<TextContentElement>
    unsavedData: string
    hasDeletedWholeText: boolean
    setUnsavedData: (value: React.SetStateAction<string>) => void
    setHasDeletedWholeText: (value: boolean) => void
    unFocusEditor: () => void
    handleAddContentExecuteOptimisticWithFallback: (
        unsavedData: string,
        textElement: TextContentElement,
        blurStartActiveTab?: number
    ) => Promise<void>
    handleContentMutationCompleted: (
        textElement: TextContentElement,
        isArticle: boolean,
        shouldSetTextContentElementToAddNull?: boolean,
        blurStartActiveTab?: number
    ) => Promise<void>
    prependKeyedTextElement: (elements: any[]) => void
    editorRef: any
    shouldTriggerNewContent: boolean
    setShouldTriggerNewContent: (value: boolean) => void
    startEditing: (contentId: string) => void
    registerNewEditor: (editor: any) => void
    registerEditorRef: (editor: any) => void
    ckeToggle: {
        activate: () => void
        deactivate: () => void
        active: boolean
    }
    setClickCoordinates: (coords: { x: number; y: number } | undefined) => void
}

const CKEDITOR_ACTIVATION_DELAY = 100

/**
 * Custom hook that provides all event handlers for the plain text editor.
 * Consolidates blur, keydown, and focus handling logic.
 */
export const usePlainTextHandlers = ({
    textElement,
    contentItem,
    unsavedData,
    hasDeletedWholeText,
    setUnsavedData,
    setHasDeletedWholeText,
    unFocusEditor,
    handleAddContentExecuteOptimisticWithFallback,
    handleContentMutationCompleted,
    prependKeyedTextElement,
    editorRef,
    shouldTriggerNewContent,
    setShouldTriggerNewContent,
    startEditing,
    ckeToggle,
    setClickCoordinates,
}: UsePlainTextHandlersParams) => {
    const chatUser = useChatProfileStore((state) => state.chatUser)
    const {
        isInProject,
        textContentElementToAdd,
        isEditingPlainText,
        setContentItemViewNodes,
        setTextContentElementToAdd,
        setIsEditingPlainText,
        contentItemViewNodes,
        treeRef,
        isTreeMappingInProgress,
    } = useShallowSelector((s) => ({
        isInProject: s.selectedProjectElementNode?.isInProject,
        textContentElementToAdd: s.textContentElementToAdd,
        isEditingPlainText: s.isEditingPlainText,
        setContentItemViewNodes: s.actions.setContentItemViewNodes,
        setTextContentElementToAdd: s.actions.setTextContentElementToAdd,
        setIsEditingPlainText: s.actions.setIsEditingPlainText,
        contentItemViewNodes: s.contentItemViewNodes,
        treeRef: s.treeRef,
        isTreeMappingInProgress: s.isTreeMappingInProgress,
    }))

    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )

    const { executeWithOptimistic } = useTransactionManager()
    const { mutateContentElements } = useContentMutationService()
    const { nodeToAdd, previousContentItemViewNodes } = useContentItemViewNode()
    const { register, handlePromoteDemoteShortcutKey } = usePromoteDemote()
    const { validateAdd, validateUpdate } = useDuplicateArticleValidation()
    const {
        isElementBeingAdded,
        markElementAsBeingAdded,
        markElementAsAdded,
        isElementRecentlyAdded,
    } = useNodeMapActions()

    // Use optimized initial data processing
    const { initialData, initialSuggestions } = useOptimizedInitialData(
        textElement
    )
    // Use refs for stable references to reduce dependency array size
    const stableRefs = useRef({
        chatUserId: chatUser?.id,
        mutateContentElements,
        executeWithOptimistic,
        prependKeyedTextElement,
        unFocusEditor,
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
        validateAdd,
        validateUpdate,
        isElementBeingAdded,
        markElementAsBeingAdded,
        markElementAsAdded,
        isElementRecentlyAdded,
    })

    // Update refs when dependencies change
    stableRefs.current = {
        chatUserId: chatUser?.id,
        mutateContentElements,
        executeWithOptimistic,
        prependKeyedTextElement,
        unFocusEditor,
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
        validateAdd,
        validateUpdate,
        isElementBeingAdded,
        markElementAsBeingAdded,
        markElementAsAdded,
        isElementRecentlyAdded,
    }

    const handleOnBlur = useCallback<ClassicEditorHandler>(
        async (_, editor) => {
            const blurStartActiveTab = useWorkspace2Store.getState().activeTab
            const refs = stableRefs.current

            const isSystemBusy =
                isSaveOperationInProgress || (isTreeMappingInProgress ?? false)
            const isElementBusy =
                refs.isElementBeingAdded(textElement.id) ||
                refs.isElementRecentlyAdded(textElement.id)

            // WORKAROUND: Block blur processing during race-prone operations
            if (isSystemBusy || isElementBusy) {
                return
            }

            const changeDetection = detectContentChanges({
                unsavedData,
                originalText: textElement.text,
                nodeToAdd,
                previousContentItemViewNodes,
                initialSuggestions,
                hasDeletedWholeText,
                textContentElementToAdd,
                isEditingPlainText,
            })

            const isBlurOnNewlyAddedEmptyContainer =
                changeDetection.shouldRevertToPrevious &&
                textContentElementToAdd?.id === textElement.id &&
                !unsavedData.trim()

            if (isBlurOnNewlyAddedEmptyContainer) {
                // Keep empty container visible - user must explicitly cancel (ESC) or type content
                return
            }

            if (changeDetection.shouldRevertToPrevious) {
                batchUpdate(() => {
                    setUnsavedData(textElement.text)
                    setTextContentElementToAdd(null)
                    setContentItemViewNodes(previousContentItemViewNodes || [])
                })
            }

            if (changeDetection.shouldDeactivate) {
                setIsEditingPlainText(false)
                return ckeToggle.deactivate()
            }
            if (changeDetection.shouldAddNewContent) {
                // Mark element immediately (synchronously) before async operation to prevent race conditions
                refs.markElementAsBeingAdded(textElement.id)

                if (
                    !(await refs.validateAdd(
                        unsavedData,
                        textElement,
                        editorRef
                    ))
                ) {
                    refs.markElementAsAdded(textElement.id)
                    return
                }

                refs.unFocusEditor()

                return await refs.handleAddContentExecuteOptimisticWithFallback(
                    unsavedData,
                    textElement,
                    blurStartActiveTab
                )
            }

            if (changeDetection.shouldExecuteOptimistic) {
                // Double-check race condition protection: Don't allow UPDATE operations during any concurrent save operations
                // This prevents content duplication when DELETE → ADD operations happen rapidly
                if (
                    isSaveOperationInProgress ||
                    refs.isElementBeingAdded(textElement.id)
                ) {
                    return
                }

                if (
                    !(await refs.validateUpdate(
                        unsavedData,
                        textElement,
                        editorRef
                    ))
                ) {
                    return
                }

                const htmlContent = editor.getData()
                let changedElements = processContent(
                    htmlContent,
                    refs.chatUserId
                )

                refs.prependKeyedTextElement(changedElements)

                const isNewElementAfterKeyedElement = textElement.id?.includes(
                    ADDITIONAL_ELEMENT_SUFFIX
                )
                const updatedTextElementId = getBaseElementId(
                    textElement.id
                ) as string

                const optimisticTreeViewNodes = replaceNodeElementsByElementId(
                    contentItemViewNodes,
                    updatedTextElementId,
                    changedElements,
                    isNewElementAfterKeyedElement
                )

                // Check if changedElements contains keyed text (text with colon)
                // Keyed paragraph format: "KeyName: text content"
                // Examples: "Key: test paragraph", "Note: important info"
                const changedElementsHasKeyedText = changedElements.some(
                    (element) => element.text?.trim().includes(':')
                )

                // Check if updating a default paragraph to keyed paragraph
                // If so, we need to remove the default paragraph from UI after server creates new content
                const shouldRemoveDefaultParagraph =
                    textElement.text === CONTENT_PLACEHOLDER_TEXT &&
                    changedElementsHasKeyedText

                refs.executeWithOptimistic({
                    fallbackState: initialData,
                    asyncAction: async () =>
                        await refs.mutateContentElements(
                            updatedTextElementId,
                            changedElements,
                            false,
                            isNewElementAfterKeyedElement,
                            blurStartActiveTab
                        ),
                    successCbFn: async () => {
                        await refs.handleContentMutationCompleted(
                            textElement,
                            nodeToAdd?.type === 'Article',
                            shouldRemoveDefaultParagraph, // Remove default paragraph when it is updated to keyed paragraph
                            blurStartActiveTab
                        )
                    },
                    errorCbFn: () => {
                        // if the optimistic update fails, we want to reset nodeToAdd so the user can retry adding again
                        if (nodeToAdd) {
                            setTextContentElementToAdd(null)
                        }
                        setContentItemViewNodes(contentItemViewNodes)
                    },
                })

                batchUpdate(() => {
                    setUnsavedData('')
                    setContentItemViewNodes(optimisticTreeViewNodes)
                    setTextContentElementToAdd(null)
                    setIsEditingPlainText(false)
                    setHasDeletedWholeText(false)
                })
                ckeToggle.deactivate()
            }
        },
        [
            isSaveOperationInProgress,
            isTreeMappingInProgress,
            unsavedData,
            textElement.text,
            textElement.id,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            textContentElementToAdd,
            isEditingPlainText,
            contentItemViewNodes,
            initialData,
            ckeToggle,
            setContentItemViewNodes,
            setTextContentElementToAdd,
            setIsEditingPlainText,
        ]
    )

    const {
        handleDeactivateAndAddContentPlainText,
        executeSaveAndAddContent,
        executeSaveOnly,
        executeEscapeKey,
        executeNodeNavigation,
    } = useKeyOperation({
        editorRef,
        contentItemId: contentItem.id,
        handleOnBlur,
        shouldTriggerNewContent,
        setShouldTriggerNewContent,
        unFocusEditor,
        setUnsavedData,
    })

    // Store the last mouse position for cursor positioning
    const lastMousePositionRef = useRef<{ x: number; y: number } | null>(null)

    /**
     * Handles mouse movement to update the last known cursor position
     * @param event - Mouse event containing the current cursor position
     */
    const handleOnMouseMove = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            requestAnimationFrame(() => {
                lastMousePositionRef.current = {
                    x: event.clientX,
                    y: event.clientY,
                }
            })
        },
        []
    )

    /**
     * Handles click events to store the click coordinates for cursor positioning
     * @param event - Mouse event containing the click coordinates
     */
    const handleOnClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (!ckeToggle.active) {
                // Store click coordinates for cursor positioning
                const { clientX: x, clientY: y } = event

                // Set coordinates for ContentEditor to use
                setClickCoordinates({ x, y })
                // Also update the ref for immediate use
                lastMousePositionRef.current = { x, y }
            }
        },
        [ckeToggle.active, setClickCoordinates]
    )

    // Reusable key handlers array - shared between keyDown and keyUp
    const keyHandlers: KeyHandler[] = useMemo(
        () => [
            // Escape key handler
            ({ event, textElementText, changeDetection }: KeyHandlerParams) =>
                executeEscapeKey(event, textElementText!, changeDetection!),

            // Save only handler (Ctrl+S)
            ({ event, changeDetection }: KeyHandlerParams) =>
                executeSaveOnly(event, changeDetection),

            // Deactivate and add content handler
            ({ event, changeDetection }: KeyHandlerParams) =>
                handleDeactivateAndAddContentPlainText(event, changeDetection!),

            // Save and add content handler (Enter)
            ({ event, changeDetection }: KeyHandlerParams) =>
                executeSaveAndAddContent(event, changeDetection),

            // Node navigation handler (Arrow keys)
            ({ event, treeRef }: KeyHandlerParams) =>
                executeNodeNavigation(event, treeRef),

            // Promote/demote handler
            ({ event }: KeyHandlerParams) => {
                handlePromoteDemoteShortcutKey(event)
                return false
            },
        ],
        [
            executeEscapeKey,
            executeSaveOnly,
            handleDeactivateAndAddContentPlainText,
            executeSaveAndAddContent,
            executeNodeNavigation,
            handlePromoteDemoteShortcutKey,
        ]
    )

    const handleOnFocusCapture = useCallback(() => {
        // Hide balloon panels immediately on focus
        hideInLineTrackTip()

        // Use the last known mouse position if coordinates aren't already set
        if (lastMousePositionRef.current && !ckeToggle.active) {
            setClickCoordinates(lastMousePositionRef.current)
        }

        setIsEditingPlainText(true)
        register(contentItem, isInProject, contentItemViewNodes)
        ckeToggle.activate()

        // Clear click coordinates after CKEditor has time to use them
        setTimeout(() => {
            setClickCoordinates(undefined)
            lastMousePositionRef.current = null
        }, CKEDITOR_ACTIVATION_DELAY)
    }, [
        setIsEditingPlainText,
        register,
        contentItem,
        isInProject,
        contentItemViewNodes,
        ckeToggle,
        setClickCoordinates,
    ])

    // KeyDown handler - for Enter, Save, and general keys
    const handleOnKeyDown = useCallback(
        createKeyHandler('down', {
            handlers: keyHandlers,
            detectContentChanges,
            unsavedData,
            originalText: textElement.text,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            treeRef,
        }),
        [
            keyHandlers,
            unsavedData,
            textElement.text,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            treeRef,
        ]
    )

    // KeyUp handler - for Escape key (more reliable)
    const handleOnKeyUp = useCallback(
        createKeyHandler('up', {
            handlers: keyHandlers,
            detectContentChanges,
            unsavedData,
            originalText: textElement.text,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            treeRef,
        }),
        [
            keyHandlers,
            unsavedData,
            textElement.text,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            treeRef,
        ]
    )

    const debouncedOnChange = useMemo(
        () =>
            withDebounce<ClassicEditorHandler>((_, editor) => {
                const hasInitialSuggestions = initialSuggestions.length > 0

                const textChanges = getUnresolvedChanges(
                    editor.getData(),
                    textElement.text,
                    hasInitialSuggestions
                )
                setUnsavedData(textChanges)

                // Mark as deleted only if original had text and now it's empty
                // This prevents false positives when clicking on already empty text
                setHasDeletedWholeText(
                    Boolean(textElement.text?.trim()) && !textChanges.trim()
                )

                // for scroll-into-content: monitor temporary content when user starts typing
                startEditing(textElement.id)
            }, 0),
        [
            textElement.text,
            textElement.id,
            initialSuggestions,
            setUnsavedData,
            setHasDeletedWholeText,
            startEditing,
        ]
    )

    // side-effect that will prevent memory leaks from unloading CKEditor
    useEffect(() => {
        return () => debouncedOnChange.cancel()
    }, [debouncedOnChange])

    return {
        handleOnBlur,
        handleOnFocusCapture,
        handleOnClick,
        handleOnMouseMove,
        handleOnKeyDown,
        handleOnKeyUp,
        debouncedOnChange,
        initialSuggestions,
        setIsEditingPlainText,
    }
}
